// The analysis board (plan §9): review a finished game move by move with a
// Stockfish evaluation of every position. A background pass evaluates each node
// (prioritising whatever the user is looking at) and feeds three things: the
// eval bar, a best-move arrow, and per-move judgement glyphs (?!/?/??) derived
// from the win-probability the mover gave up. Games come either from the server
// (`/analysis/game/:id`) or from a local bot game (`/analysis/bot/:id`).
//
// Once that sweep finishes the board switches to *continuous thinking*: the
// engine is pointed at whatever position is on screen and keeps searching it,
// streaming a deepening eval and refreshed MultiPV lines, until you navigate
// away (then it re-aims at the new node). How deep it goes — and whether it ever
// stops — is the user's `engine/analysisDepth.ts` setting. One engine, one search
// at a time: the sweep and the thinker hand the worker back and forth via
// `Engine.stop()`, and `passActive` is the flag that decides who owns it.

import { Chessground } from "chessground";
import type { Api as CgApi } from "chessground/api";
import type { DrawShape } from "chessground/draw";
import type { Key } from "chessground/types";

import { api, ApiError } from "../api.ts";
import { currentToken, isCurrent } from "../router.ts";
import { esc, h } from "../dom.ts";
import { checkColor, isPromotion, legalDests, parsePosition, uciSquares } from "../game/board.ts";
import { getBotGame } from "../game/botHistory.ts";
import type { Color } from "../game/types.ts";
import {
  buildReview,
  childNode,
  isTypingTarget,
  navControls,
  navKey,
  pvToSan,
  scrollIntoListView,
  type GameNode,
  type NavAction,
  type Review,
} from "../game/review.ts";
import {
  buildPgn,
  metaFromBotGame,
  metaFromServerGame,
  pgnFilename,
  pgnResultLine,
  terminationLabel,
  type PgnMeta,
} from "../game/pgn.ts";
import { getStudy } from "../game/studyStore.ts";
import { lookupOpening } from "../game/openings.ts";
import { copyText, downloadText } from "../ui/clipboard.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { promotionCard } from "../ui/promotion.ts";
import { t } from "../i18n/index.ts";
import { Engine, type SearchInfo } from "../engine/stockfish.ts";
import {
  analysisDepth,
  onAnalysisDepthChange,
  sweepDepth,
  type DepthLevel,
} from "../engine/analysisDepth.ts";
import {
  formatEval,
  judgeMove,
  judgementGlyph,
  povWinProb,
  whitePovScore,
  winProbWhite,
  type Judgement,
  type Score,
} from "../engine/eval.ts";

const MOVETIME_CAP = 1500; // ms ceiling so a sharp position can't stall the pass

let active: { destroy: () => void } | null = null;

/** Tear down the on-screen analysis board immediately — e.g. before an async
 *  fetch in a screen that will mount a new one — so its Stockfish worker and
 *  global keydown listener don't outlive the navigation (rather than lingering
 *  until the replacement board's element swaps in). */
export function destroyActive(): void {
  active?.destroy();
  active = null;
}

interface Meta {
  white: string;
  black: string;
  orientation: Color;
  title: string;
  resultLine: string;
  /** "<ECO> · <Opening>" line for the header, filled by the opening lookup (C2). */
  openingLine?: string;
}

const sameTarget = (a: EvalTarget, b: EvalTarget): boolean => a.where === b.where && a.idx === b.idx;

/** Evaluation of one node, normalised to White's perspective. */
interface NodeEval {
  scoreWhite: Score;
  bestUci?: string;
  depth: number;
  /** Up to MultiPV engine lines for this position (white-POV scores). */
  lines?: { scoreWhite: Score; pv: string[] }[];
}

/** Which node an engine result belongs to: the mainline or the scratch line. */
interface EvalTarget {
  where: "main" | "line";
  idx: number;
}

export async function analysisScreen(source: "server" | "bot", id: string): Promise<HTMLElement> {
  const token = currentToken();
  active?.destroy();
  active = null;

  let moves: { uci: string }[];
  let meta: Meta;
  let pgnMeta: PgnMeta;

  if (source === "server") {
    try {
      const full = await api.getGame(id);
      moves = full.moves;
      meta = {
        white: full.white.name,
        black: full.black.name,
        orientation: "white",
        title: `${full.white.name} vs ${full.black.name}`,
        resultLine: serverResultLine(full.status, full.winner, full.termination),
      };
      pgnMeta = metaFromServerGame(full);
    } catch (err) {
      return notice(err instanceof ApiError ? err.message : t("game.loadFailed"));
    }
  } else {
    const rec = getBotGame(id);
    if (!rec) return notice(t("analysis.botGameGone"));
    moves = rec.moves;
    const you = t("common.you");
    const comp = t("chess.computer");
    meta = {
      white: rec.humanColor === "white" ? you : comp,
      black: rec.humanColor === "black" ? you : comp,
      orientation: rec.humanColor,
      title: `${you} vs ${comp} · ${t("levels.levelN", { n: rec.level })}`,
      resultLine: botResultLine(rec.status, rec.winner, rec.humanColor),
    };
    pgnMeta = metaFromBotGame(rec);
  }

  const review = buildReview(moves);
  return mountAnalysis(review, meta, pgnMeta, token);
}

/** Study a game imported into this browser's local library (plan B4). Same board,
 *  resolved from `studyStore` instead of the server. */
export function studyGameScreen(id: string): HTMLElement {
  const token = currentToken();
  active?.destroy();
  active = null;
  const g = getStudy(id);
  if (!g) return notice(t("analysis.studyGone"));
  return mountFromPgn(buildReview(g.moves, g.initialFen), g.meta, "white", token);
}

/** Mount the engine board for a review whose metadata is a `PgnMeta` (study /
 *  collection games). Derives the display `Meta` from the PGN headers. `token`
 *  is the caller's navigation generation (see `mountAnalysis`). */
export function mountFromPgn(
  review: Review,
  pgnMeta: PgnMeta,
  orientation: Color = "white",
  token: number = currentToken(),
): HTMLElement {
  const meta: Meta = {
    white: pgnMeta.white,
    black: pgnMeta.black,
    orientation,
    title: `${pgnMeta.white} vs ${pgnMeta.black}`,
    resultLine: pgnResultLine(pgnMeta),
  };
  return mountAnalysis(review, meta, pgnMeta, token);
}

/** Build the board layout and start the controller for a resolved review. The
 *  source-agnostic mount shared by server, bot, study and collection games. */
export function mountAnalysis(
  review: Review,
  meta: Meta,
  pgnMeta: PgnMeta,
  token: number = currentToken(),
): HTMLElement {
  const el = buildLayout(meta);
  // A navigation that awaited (e.g. a slow `getGame`) while a newer navigation
  // mounted its board must not tear that live board down. If we've been
  // superseded, return a detached layout — the router discards it on its own
  // stale-token check — and touch no shared state.
  if (!isCurrent(token)) return el;

  active?.destroy();
  active = null;
  requestAnimationFrame(() => {
    // The frame can fire after `el` was committed then replaced by a newer
    // navigation (before this observer existed). Only construct — and thus spin
    // up an engine worker + document keydown listener — for a board that is
    // actually on screen; otherwise it would leak for the tab's lifetime.
    if (!el.isConnected) return;
    const ctrl = new AnalysisCtrl(el, review, meta, pgnMeta);
    active = ctrl;
    onDisconnect(el, () => {
      ctrl.destroy();
      if (active === ctrl) active = null;
    });
  });
  // Name the opening asynchronously (the table lazy-loads): fill the header and
  // back-fill ECO/Opening into the export meta (without clobbering imported tags).
  void lookupOpening(review).then((hit) => {
    if (!hit) return;
    const box = el.querySelector<HTMLElement>(".opening-line");
    if (box && el.isConnected) {
      box.textContent = `${hit.eco} · ${hit.name}`;
      box.hidden = false;
    }
    if (!pgnMeta.eco) pgnMeta.eco = hit.eco;
    if (!pgnMeta.opening) pgnMeta.opening = hit.name;
  });
  return el;
}

// ---------------------------------------------------------------------------

class AnalysisCtrl {
  private cg: CgApi;
  private review: Review;
  private nodes: GameNode[];
  private evals: (NodeEval | undefined)[];
  private current = 0;
  private engine = new Engine();
  private orientation: Color;
  private destroyed = false;
  private onKey: (e: KeyboardEvent) => void;

  // Scratch variation (plan C1): one active line branched off the mainline.
  private line: GameNode[] = [];
  private lineEvals: (NodeEval | undefined)[] = [];
  private lineBase = -1; // mainline node index the line branches FROM
  private inLine = false; // is the current view inside the line?
  private lineCurrent = 0; // index into `line` when inLine

  // Engine pass state: the loop restarts whenever new nodes appear.
  private passActive = false;
  private engineUp: Promise<boolean> | null = null;
  private engineOk = true;

  // Continuous thinking: the sweep runs first (it must end for the judgement
  // glyphs to appear), then `runPass` keeps handing the viewed node to `think`.
  private level: DepthLevel = analysisDepth();
  private sweepDone = false;
  private passDirty = false; // a depth change asked for a re-sweep mid-pass
  private thinkTarget: EvalTarget | null = null;
  private paintQueued = false;
  private offDepth: () => void;
  /** Bumped when the depth setting changes, so searches started under the old
   *  setting can't write their (now-stale) results over the invalidated evals. */
  private epoch = 0;

  constructor(
    private root: HTMLElement,
    review: Review,
    meta: Meta,
    private pgnMeta: PgnMeta,
  ) {
    this.review = review;
    this.nodes = review.nodes;
    this.evals = new Array(this.nodes.length).fill(undefined);
    this.orientation = meta.orientation;

    const boardEl = root.querySelector<HTMLElement>(".cg-board-wrap")!;
    this.cg = Chessground(boardEl, {
      fen: this.nodes[0].fen,
      orientation: this.orientation,
      highlight: { lastMove: true, check: true },
      animation: { enabled: true, duration: 160 },
      drawable: { enabled: false, visible: true },
      movable: {
        free: false,
        color: undefined,
        dests: new Map(),
        showDests: true,
        events: { after: (orig, dest) => this.onUserMove(orig, dest) },
      },
      premovable: { enabled: false },
      draggable: { showGhost: true },
    });

    this.renderMoveList();
    this.wireNav();
    this.onKey = (e) => this.handleKey(e);
    document.addEventListener("keydown", this.onKey);
    this.offDepth = onAnalysisDepthChange((level) => this.onDepthChange(level));

    this.goTo(this.nodes.length - 1); // open on the final position
    void this.runPass();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    document.removeEventListener("keydown", this.onKey);
    this.offDepth();
    this.engine.destroy(); // settles the in-flight (possibly infinite) search
    this.cg.destroy();
  }

  /** The Settings depth changed under us: re-sweep anything now too shallow, and
   *  re-aim the thinker. A *lower* depth keeps the deeper evals it already has. */
  private onDepthChange(level: DepthLevel): void {
    this.level = level;
    this.epoch += 1;
    const want = sweepDepth(level);
    for (let i = 0; i < this.evals.length; i++) {
      if ((this.evals[i]?.depth ?? 0) < want) this.evals[i] = undefined;
    }
    this.sweepDone = false;
    this.passDirty = true; // an in-flight pass restarts from its `finally`
    if (this.thinkTarget) this.engine.stop(); // never interrupt a sweep search
    this.paintStatus(); // else the panel claims "complete" until the first node lands
    void this.runPass();
  }

  // --- navigation -----------------------------------------------------

  /** The node the board is showing (mainline or scratch line). */
  private currentNode(): GameNode {
    return this.inLine ? this.line[this.lineCurrent] : this.nodes[this.current];
  }

  private goTo(index: number): void {
    this.inLine = false;
    this.current = Math.max(0, Math.min(this.nodes.length - 1, index));
    this.showCurrent();
  }

  /** Jump to a node of the scratch line (no-op when there is no line). */
  private goLine(index: number): void {
    if (this.line.length === 0) return;
    this.inLine = true;
    this.lineCurrent = Math.max(0, Math.min(this.line.length - 1, index));
    this.showCurrent(); // `retarget` picks up a line node that still needs an eval
  }

  /** Paint the board + side panel for `currentNode()`. */
  private showCurrent(): void {
    const node = this.currentNode();
    const pos = parsePosition(node.fen);
    this.cg.set({
      fen: node.fen,
      lastMove: node.uci ? uciSquares(node.uci) : undefined,
      check: pos ? checkColor(pos) : undefined,
    });
    this.syncMovable(pos);
    this.paintEval();
    this.paintComment(node);
    this.highlightCurrent();
    this.syncLineUi();
    this.retarget(); // aim the continuous search at the node now on screen
  }

  /** The imported annotation of the viewed move, when it has one (plan B1). */
  private paintComment(node: GameNode): void {
    const box = this.root.querySelector<HTMLElement>(".move-comment");
    if (!box) return;
    box.textContent = node.comment ?? "";
    box.hidden = !node.comment;
  }

  /** Let whoever is to move in the viewed position pick up pieces. */
  private syncMovable(pos = parsePosition(this.currentNode().fen)): void {
    const node = this.currentNode();
    this.cg.set({
      turnColor: node.turn,
      movable: {
        color: pos ? node.turn : undefined,
        dests: pos ? legalDests(pos) : new Map(),
      },
    });
  }

  private navTo(action: NavAction): void {
    if (this.inLine) {
      switch (action) {
        case "first":
          this.goTo(0);
          return;
        case "last":
          this.goTo(this.nodes.length - 1);
          return;
        case "prev":
          if (this.lineCurrent === 0) this.goTo(this.lineBase);
          else this.goLine(this.lineCurrent - 1);
          return;
        case "next":
          this.goLine(this.lineCurrent + 1);
          return;
      }
    }
    const last = this.nodes.length - 1;
    this.goTo(
      action === "first" ? 0 : action === "last" ? last : action === "prev" ? this.current - 1 : this.current + 1,
    );
  }

  private wireNav(): void {
    const nav = this.root.querySelector<HTMLElement>(".nav")!;
    nav.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("button[data-nav]");
      if (!btn) return;
      const action = btn.getAttribute("data-nav");
      if (action === "flip") {
        this.orientation = this.orientation === "white" ? "black" : "white";
        this.cg.set({ orientation: this.orientation });
        this.root.querySelector(".eval-bar")!.classList.toggle("flipped", this.orientation === "black");
        return;
      }
      this.navTo(action as NavAction);
    });

    this.root.querySelector<HTMLElement>(".movelist")!.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.closest(".var-x")) {
        this.discardLine();
        return;
      }
      const varSpan = target.closest("[data-var-ply]");
      if (varSpan) {
        this.goLine(Number(varSpan.getAttribute("data-var-ply")));
        return;
      }
      const span = target.closest("[data-ply]");
      if (span) this.goTo(Number(span.getAttribute("data-ply")));
    });

    const actions = this.root.querySelector<HTMLElement>(".actions");
    actions?.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-act]");
      if (btn) void this.runAction(btn);
    });

    // Clicking an engine line plays its first move as a variation (plan C2).
    this.root.querySelector<HTMLElement>(".engine-lines")?.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-line-uci]");
      if (btn?.dataset.lineUci) this.playUserMove(btn.dataset.lineUci);
    });

    this.root.querySelector<HTMLButtonElement>(".back-to-game")?.addEventListener("click", () => {
      this.discardLine();
    });
  }

  // --- scratch line (plan C1) ------------------------------------------

  private onUserMove(orig: Key, dest: Key): void {
    const pos = parsePosition(this.currentNode().fen);
    if (!pos) {
      this.showCurrent();
      return;
    }
    if (isPromotion(pos, orig, dest)) {
      void this.askPromotion(pos.turn as Color).then((code) => {
        if (!code) {
          this.showCurrent(); // cancelled → restore the position
          return;
        }
        this.playUserMove(orig + dest + code);
      });
    } else {
      this.playUserMove(orig + dest);
    }
  }

  /** Apply a user (or engine-line) move at the viewed position: step forward if
   *  it matches the known continuation, else branch/extend the scratch line.
   *  Moves are compared by resulting position, not UCI (castling encodings). */
  private playUserMove(uci: string): void {
    const next = childNode(this.currentNode(), uci);
    if (!next) {
      this.showCurrent(); // illegal or unparsable → snap back
      return;
    }

    if (!this.inLine) {
      const mainNext = this.nodes[this.current + 1];
      if (mainNext && mainNext.fen === next.fen) {
        this.goTo(this.current + 1); // same move — no branch
        return;
      }
      this.lineBase = this.current;
      this.line = [next];
      this.lineEvals = [undefined];
      this.inLine = true;
      this.lineCurrent = 0;
    } else {
      const lineNext = this.line[this.lineCurrent + 1];
      if (lineNext && lineNext.fen === next.fen) {
        this.goLine(this.lineCurrent + 1); // walking the line
        return;
      }
      // Append at the tip, or truncate a diverging tail.
      this.line = this.line.slice(0, this.lineCurrent + 1).concat(next);
      this.lineEvals = this.lineEvals.slice(0, this.lineCurrent + 1).concat(undefined);
      this.lineCurrent += 1;
    }
    this.renderMoveList();
    this.goLine(this.lineCurrent);
  }

  /** Drop the scratch line and return to the branch point. */
  private discardLine(): void {
    if (this.line.length === 0) return;
    const base = this.lineBase;
    this.line = [];
    this.lineEvals = [];
    this.inLine = false;
    this.lineBase = -1;
    this.renderMoveList();
    this.goTo(base);
  }

  private askPromotion(color: Color): Promise<string | null> {
    return new Promise((resolve) => {
      const overlay = this.root.querySelector<HTMLElement>(".promotion");
      if (!overlay) {
        resolve("q");
        return;
      }
      const card = promotionCard(color, (code) => {
        overlay.hidden = true;
        resolve(code);
      });
      overlay.replaceChildren(card);
      overlay.hidden = false;
    });
  }

  /** Show/hide the "Back to game" affordance with the line view. */
  private syncLineUi(): void {
    const btn = this.root.querySelector<HTMLButtonElement>(".back-to-game");
    if (btn) btn.hidden = !this.inLine;
  }

  /** Copy PGN · Download .pgn · Copy FEN, with transient button feedback. */
  private async runAction(btn: HTMLButtonElement): Promise<void> {
    switch (btn.getAttribute("data-act")) {
      case "copy-pgn": {
        const ok = await copyText(buildPgn(this.review, this.pgnMeta));
        flash(btn, ok ? t("analysis.copied") : t("analysis.copyFailed"));
        break;
      }
      case "download-pgn": {
        downloadText(pgnFilename(this.pgnMeta), buildPgn(this.review, this.pgnMeta));
        flash(btn, t("analysis.saved"));
        break;
      }
      case "copy-fen": {
        const ok = await copyText(this.nodes[this.current].fen);
        flash(btn, ok ? t("analysis.copied") : t("analysis.copyFailed"));
        break;
      }
    }
  }

  private handleKey(e: KeyboardEvent): void {
    if (isTypingTarget(e.target)) return;
    const action = navKey(e);
    if (!action) return;
    e.preventDefault();
    this.navTo(action);
  }

  // --- evaluation pass + continuous thinking ---------------------------

  /** Bring the engine up once (MultiPV 3 for the lines panel, plan C2). */
  private ensureEngine(): Promise<boolean> {
    if (!this.engineUp) {
      this.engineUp = this.engine
        .init()
        .then(() => this.engine.setOption("MultiPV", 3))
        .then(() => true)
        .catch(() => {
          this.engineOk = false;
          this.paintStatus();
          return false;
        });
    }
    return this.engineUp;
  }

  /**
   * The sole owner of the engine worker. Sweeps every unevaluated node at the
   * configured depth, then keeps thinking about whatever node is in view. Guarded
   * by `passActive`, so it is safe to call whenever new nodes appear (a
   * scratch-line move) or the view moves — it restarts if it had settled.
   *
   * Because nothing else issues a `go`, a `stop()` from `retarget()` can only
   * ever interrupt the thinker, never a sweep search.
   */
  private async runPass(): Promise<void> {
    if (this.passActive) return;
    this.passActive = true;
    try {
      if (!(await this.ensureEngine())) return;
      while (!this.destroyed) {
        const target = this.nextToEval();
        if (target !== null) {
          try {
            await this.evalNode(target);
          } catch {
            // Don't let one bad position stall the sweep — mark it evaluated (neutral).
            if (this.evalAt(target) === undefined) this.store(target, { scoreWhite: { cp: 0 }, depth: 0 });
          }
          continue;
        }
        this.sweepDone = true;
        const viewed = this.viewedTarget();
        if (!this.needsThink(viewed)) break;
        await this.think(viewed);
        // `think` settles either because the search hit its depth (we're done with
        // this node) or because `retarget()` stopped it — in which case the view
        // has already moved and the next turn of the loop aims at the new node.
        if (this.destroyed || sameTarget(this.viewedTarget(), viewed)) break;
      }
    } finally {
      this.passActive = false;
      this.thinkTarget = null;
      if (!this.destroyed) this.paintStatus();
      if (this.passDirty && !this.destroyed) {
        this.passDirty = false;
        void this.runPass();
      }
    }
  }

  /** Point the engine at the node that just came into view. */
  private retarget(): void {
    if (this.destroyed || !this.sweepDone) return; // the sweep hands over when it ends
    if (this.thinkTarget) {
      if (!sameTarget(this.thinkTarget, this.viewedTarget())) this.engine.stop();
      return; // `runPass` re-aims once the stopped search settles
    }
    if (!this.passActive) void this.runPass();
  }

  /** Search the viewed node, streaming a deepening eval and fresh lines into the
   *  panel. Resolves at `level.depth`, or — when the user asked for no limit —
   *  only once `stop()` is called by navigation or teardown. */
  private async think(target: EvalTarget): Promise<void> {
    const node = this.nodeAt(target);
    if (!node) return; // the line was discarded between `needsThink` and here
    const epoch = this.epoch;
    const fresh = () => !this.destroyed && epoch === this.epoch && this.nodeAt(target) === node;

    this.thinkTarget = target;
    this.paintStatus();
    const res = await this.engine.go({
      fen: node.fen,
      ...(this.level.depth ? { depth: this.level.depth } : { infinite: true }),
      onInfos: (infos) => {
        if (!fresh() || infos.length === 0) return;
        this.put(target, this.evalOf(infos, node.turn));
        if (this.isViewed(target)) this.schedulePaint();
      },
    });
    this.thinkTarget = null;
    if (!fresh() || !res.infos?.length) return;
    this.store(target, this.evalOf(res.infos, node.turn, res.bestmove));
  }

  /** Is there anything left for the engine to learn about `target`? */
  private needsThink(target: EvalTarget): boolean {
    const node = this.nodeAt(target);
    if (!node) return false;
    const pos = parsePosition(node.fen);
    if (!pos || pos.isEnd()) return false; // mate/stalemate: no move to search for
    if (!this.level.depth) return true; // "unlimited" — never satisfied
    return (this.evalAt(target)?.depth ?? 0) < this.level.depth;
  }

  /** Prefer the node in view (scratch line included); then the mainline scan. */
  private nextToEval(): EvalTarget | null {
    if (this.inLine && this.lineEvals[this.lineCurrent] === undefined) {
      return { where: "line", idx: this.lineCurrent };
    }
    if (!this.inLine && this.evals[this.current] === undefined) {
      return { where: "main", idx: this.current };
    }
    for (let i = 0; i < this.nodes.length; i++) if (this.evals[i] === undefined) return { where: "main", idx: i };
    return null;
  }

  private viewedTarget(): EvalTarget {
    return this.inLine ? { where: "line", idx: this.lineCurrent } : { where: "main", idx: this.current };
  }

  private nodeAt(target: EvalTarget): GameNode {
    return target.where === "main" ? this.nodes[target.idx] : this.line[target.idx];
  }

  private evalAt(target: EvalTarget): NodeEval | undefined {
    return target.where === "main" ? this.evals[target.idx] : this.lineEvals[target.idx];
  }

  private isViewed(target: EvalTarget): boolean {
    return sameTarget(target, this.viewedTarget());
  }

  private async evalNode(target: EvalTarget): Promise<void> {
    const node = this.nodeAt(target);
    if (!node) return; // the line was truncated/discarded while queued
    const pos = parsePosition(node.fen);
    const depth = sweepDepth(this.level);

    // Terminal positions need no search.
    if (pos?.isCheckmate()) {
      this.store(target, { scoreWhite: { mate: node.turn === "white" ? -1 : 1 }, depth });
      return;
    }
    if (pos && (pos.isStalemate() || pos.isInsufficientMaterial())) {
      this.store(target, { scoreWhite: { cp: 0 }, depth });
      return;
    }

    const epoch = this.epoch;
    const fresh = () => !this.destroyed && epoch === this.epoch && this.nodeAt(target) === node;
    const res = await this.engine.go({
      fen: node.fen,
      depth,
      movetimeMs: MOVETIME_CAP,
      onInfos: this.isViewed(target)
        ? (infos) => {
            if (!fresh() || infos.length === 0) return;
            this.put(target, this.evalOf(infos, node.turn));
            if (this.isViewed(target)) this.schedulePaint();
          }
        : undefined,
    });
    // A truncated/re-extended line can put a different node at this index by the
    // time the search lands — only store onto the node we actually searched.
    if (!fresh()) return;
    if (!res.infos?.length) {
      this.store(target, { scoreWhite: { cp: 0 }, depth: 0 });
      return;
    }
    this.store(target, this.evalOf(res.infos, node.turn, res.bestmove));
  }

  /** MultiPV infos (rank order) → the node's White-perspective evaluation. */
  private evalOf(infos: SearchInfo[], turn: Color, bestmove?: string | null): NodeEval {
    const top = infos[0];
    return {
      scoreWhite: whitePovScore(top.score, turn),
      bestUci: bestmove ?? top.pv[0],
      depth: top.depth,
      lines: infos.map((info) => ({ scoreWhite: whitePovScore(info.score, turn), pv: info.pv })),
    };
  }

  /** Record an evaluation without repainting — for the live stream, which lands
   *  many times a second and must not rebuild the move list on every info line. */
  private put(target: EvalTarget, ev: NodeEval): void {
    if (target.where === "main") this.evals[target.idx] = ev;
    else if (target.idx < this.line.length) this.lineEvals[target.idx] = ev;
  }

  private store(target: EvalTarget, ev: NodeEval): void {
    if (target.where !== "main" && target.idx >= this.line.length) return; // line discarded
    this.put(target, ev);
    if (this.isViewed(target)) this.paintEval();
    // A judgement needs both sides of a move, so refresh the (small) list wholesale.
    this.renderMoveList();
    this.highlightCurrent();
    this.updateSummary();
    this.paintStatus();
  }

  /** Coalesce the live stream's repaints to one per frame. */
  private schedulePaint(): void {
    if (this.paintQueued) return;
    this.paintQueued = true;
    requestAnimationFrame(() => {
      this.paintQueued = false;
      if (!this.destroyed) this.paintEval();
    });
  }

  // --- painting -------------------------------------------------------

  private currentEval(): NodeEval | undefined {
    return this.inLine ? this.lineEvals[this.lineCurrent] : this.evals[this.current];
  }

  /** Update the eval bar + best-move arrow + numeric readout for the current node. */
  private paintEval(): void {
    const ev = this.currentEval();
    if (ev) {
      this.paintBar(ev.scoreWhite);
      this.setEval(formatEval(ev.scoreWhite));
      const shapes: DrawShape[] = [];
      if (ev.bestUci && ev.bestUci.length >= 4) {
        shapes.push({
          orig: ev.bestUci.slice(0, 2) as Key,
          dest: ev.bestUci.slice(2, 4) as Key,
          brush: "paleBlue",
        });
      }
      this.cg.setAutoShapes(shapes);
    } else {
      this.setEval("…");
      this.cg.setAutoShapes([]);
    }
    this.paintLines();
    this.paintBestWas();
    this.paintStatus();
  }

  /** Up to 3 engine lines in human SAN for the viewed position (plan C2). */
  private paintLines(): void {
    const box = this.root.querySelector<HTMLElement>(".engine-lines");
    if (!box) return;
    box.replaceChildren();
    const ev = this.currentEval();
    if (!ev?.lines?.length) return; // nothing until evaluated — no skeletons
    const fen = this.currentNode().fen;
    for (const line of ev.lines.slice(0, 3)) {
      if (!line.pv[0]) continue;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "eline";
      btn.dataset.lineUci = line.pv[0];
      btn.innerHTML =
        `<span class="eline-eval">${esc(formatEval(line.scoreWhite))}</span>` +
        `<span class="eline-moves">${esc(pvToSan(fen, line.pv))}</span>`;
      box.append(btn);
    }
  }

  /** "Best was …" under the lines, for a judged mainline move (plan C2). */
  private paintBestWas(): void {
    const el = this.root.querySelector<HTMLElement>(".best-was");
    if (!el) return;
    const j = !this.inLine && this.current > 0 ? this.judgementAt(this.current) : null;
    const bestUci = j ? this.evals[this.current - 1]?.bestUci : undefined;
    if (!j || !bestUci) {
      el.hidden = true;
      el.textContent = "";
      return;
    }
    el.hidden = false;
    el.textContent = t("analysis.bestWas", { move: pvToSan(this.nodes[this.current - 1].fen, [bestUci], 1) });
  }

  private paintBar(scoreWhite: Score): void {
    const pct = winProbWhite(scoreWhite); // 0..100, White at the bottom
    const fill = this.root.querySelector<HTMLElement>(".eval-fill");
    if (fill) fill.style.height = `${pct}%`;
    const tag = this.root.querySelector<HTMLElement>(".eval-tag");
    if (tag) tag.textContent = formatEval(scoreWhite);
  }

  private setEval(text: string): void {
    const el = this.root.querySelector<HTMLElement>(".eval-num");
    if (el) el.textContent = text;
  }

  /** What the engine is doing right now, from the viewed node's point of view. */
  private statusText(): string {
    if (!this.engineOk) return t("analysis.engineUnavailable");
    const depth = this.currentEval()?.depth ?? 0;
    if (depth === 0) return t("analysis.startingEngine");
    if (this.thinkTarget) return t("analysis.thinking", { depth });
    if (!this.sweepDone) return t("analysis.bestLine", { depth });
    return t("analysis.done", { depth });
  }

  private paintStatus(): void {
    const done = this.evals.filter((e) => e !== undefined).length;
    const el = this.root.querySelector<HTMLElement>(".engine-status");
    if (el) el.textContent = `${this.statusText()} — ${t("analysis.positions", { done, total: this.nodes.length })}`;
  }

  // --- move list + summary --------------------------------------------

  private renderMoveList(): void {
    const list = this.root.querySelector<HTMLElement>(".movelist")!;
    list.replaceChildren();
    for (let i = 1; i < this.nodes.length; i += 2) {
      const row = document.createElement("div");
      row.className = "move-row";
      row.innerHTML = `<span class="mn">${(i - 1) / 2 + 1}.</span>`;
      row.append(this.moveSpan(i));
      const black = i + 1 < this.nodes.length ? this.moveSpan(i + 1) : blankSpan();
      row.append(black);
      list.append(row);
    }
    this.renderVarline(list);
  }

  /** The scratch line, indented under the row it branches from (plan C1). */
  private renderVarline(list: HTMLElement): void {
    if (this.line.length === 0) return;
    const varEl = document.createElement("div");
    varEl.className = "varline";
    let moves = "";
    this.line.forEach((n, k) => {
      const ply = this.lineBase + 1 + k;
      const num = Math.ceil(ply / 2);
      const prefix = n.moveColor === "white" ? `${num}. ` : k === 0 ? `${num}… ` : "";
      moves += `${prefix}<span class="san" data-var-ply="${k}">${esc(n.san ?? "")}</span> `;
    });
    varEl.innerHTML =
      `<button class="var-x" type="button" title="${esc(t("analysis.discardLine"))}"` +
      ` aria-label="${esc(t("analysis.discardLine"))}">×</button>` +
      `<span class="var-moves">( ${moves})</span>`;
    // After the row holding the ply this line replaces; at the end for a branch
    // at the tail (lineBase is the last mainline node).
    const anchor = list.querySelector(`[data-ply="${this.lineBase + 1}"]`)?.closest(".move-row");
    if (anchor) anchor.after(varEl);
    else list.append(varEl);
  }

  /** One half-move span (`data-ply` = node index it navigates to). */
  private moveSpan(ply: number): HTMLElement {
    const node = this.nodes[ply];
    const span = document.createElement("span");
    span.className = "san";
    span.setAttribute("data-ply", String(ply));
    const j = this.judgementAt(ply);
    span.textContent = node.san ?? "";
    if (j) {
      const g = document.createElement("span");
      g.className = `glyph ${j}`;
      g.textContent = judgementGlyph(j);
      span.append(g);
      span.classList.add(j);
    }
    return span;
  }

  /** Judge the move leading into node `ply`, if both endpoints are evaluated. */
  private judgementAt(ply: number): Judgement | null {
    const before = this.evals[ply - 1];
    const after = this.evals[ply];
    const node = this.nodes[ply];
    if (!before || !after || !node.moveColor) return null;
    const beforeWin = povWinProb(before.scoreWhite, node.moveColor);
    const afterWin = povWinProb(after.scoreWhite, node.moveColor);
    return judgeMove(beforeWin, afterWin);
  }

  private highlightCurrent(): void {
    const list = this.root.querySelector<HTMLElement>(".movelist")!;
    list.querySelectorAll(".san.cur").forEach((s) => s.classList.remove("cur"));
    const cur = this.inLine
      ? list.querySelector<HTMLElement>(`[data-var-ply="${this.lineCurrent}"]`)
      : list.querySelector<HTMLElement>(`[data-ply="${this.current}"]`);
    if (cur) {
      cur.classList.add("cur");
      scrollIntoListView(list, cur);
    }
  }

  private updateSummary(): void {
    const counts: Record<Color, Record<Judgement, number>> = {
      white: { blunder: 0, mistake: 0, inaccuracy: 0 },
      black: { blunder: 0, mistake: 0, inaccuracy: 0 },
    };
    for (let ply = 1; ply < this.nodes.length; ply++) {
      const j = this.judgementAt(ply);
      const color = this.nodes[ply].moveColor;
      if (j && color) counts[color][j]++;
    }
    const box = this.root.querySelector<HTMLElement>(".summary")!;
    box.replaceChildren();
    for (const color of ["white", "black"] as Color[]) {
      const c = counts[color];
      const row = document.createElement("div");
      row.className = "sum-row";
      row.innerHTML =
        `<span class="sum-name">${color === "white" ? t("chess.white") : t("chess.black")}</span>` +
        `<span class="sum-stat blunder">${c.blunder} ✗</span>` +
        `<span class="sum-stat mistake">${c.mistake} ?</span>` +
        `<span class="sum-stat inaccuracy">${c.inaccuracy} ?!</span>`;
      box.append(row);
    }
  }
}

// ---------------------------------------------------------------------------
// Layout + result formatting
// ---------------------------------------------------------------------------

function buildLayout(meta: Meta): HTMLElement {
  const el = h(`
    <main class="analysis">
      <div class="board-col">
        <div class="eval-bar ${meta.orientation === "black" ? "flipped" : ""}">
          <div class="eval-fill"></div>
          <span class="eval-tag"></span>
        </div>
        <div class="cg-board-wrap"></div>
        <div class="promotion" hidden></div>
      </div>
      <aside class="side">
        <div class="side-top">
          <span class="title">${esc(meta.title)}</span>
          <a class="btn ghost" data-link href="/">${t("common.home")}</a>
        </div>
        <div class="opening-line"${meta.openingLine ? "" : " hidden"}>${esc(meta.openingLine ?? "")}</div>
        <div class="eval-readout"><span class="eval-num">…</span> <span class="result-line">${esc(meta.resultLine)}</span></div>
        <div class="move-comment fine" hidden></div>
        <div class="engine-lines"></div>
        <p class="best-was fine" hidden></p>
        <div class="actions">
          <button class="btn ghost" data-act="copy-pgn" type="button">${t("analysis.copyPgn")}</button>
          <button class="btn ghost" data-act="download-pgn" type="button">${t("analysis.downloadPgn")}</button>
          <button class="btn ghost" data-act="copy-fen" type="button">${t("analysis.copyFen")}</button>
        </div>
        <p class="engine-status">${t("analysis.startingEngine")}</p>
        <div class="movelist analysis-moves"></div>
        <div class="nav-slot"></div>
        <div class="summary"></div>
      </aside>
    </main>
  `);
  const navSlot = el.querySelector<HTMLElement>(".nav-slot")!;
  navSlot.append(navControls({ flip: true }));
  const back = h(`<button class="btn ghost back-to-game" type="button" hidden>${t("analysis.backToGame")}</button>`);
  navSlot.append(back);
  return el;
}

function serverResultLine(status: string, winner: Color | null, reason?: string | null): string {
  if (status === "started") return t("analysis.gameInProgress");
  const key = reason || status;
  const detail = terminationLabel(key);
  if (!winner) return `${t("pgn.draw")} · ${detail}`;
  return `${winner === "white" ? t("pgn.whiteWins") : t("pgn.blackWins")} · ${detail}`;
}

function botResultLine(status: string, winner: Color | null, human: Color): string {
  const detail = terminationLabel(status);
  if (!winner) return `${t("pgn.draw")} · ${detail}`;
  return `${winner === human ? t("result.youWon") : t("result.youLost")} · ${detail}`;
}

function blankSpan(): HTMLElement {
  const s = document.createElement("span");
  s.className = "san";
  return s;
}

/** Briefly swap a button's label to confirm an action, then restore it. */
function flash(btn: HTMLButtonElement, text: string): void {
  if (btn.dataset.orig === undefined) btn.dataset.orig = btn.textContent ?? "";
  btn.textContent = text;
  btn.classList.add("flashed");
  window.setTimeout(() => {
    btn.textContent = btn.dataset.orig ?? "";
    btn.classList.remove("flashed");
  }, 1200);
}

function notice(body: string): HTMLElement {
  return h(`
    <main class="card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="msg bad">${esc(body)}</p>
      <p><a class="btn ghost" data-link href="/">${t("common.backHome")}</a></p>
    </main>
  `);
}
